import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { formatFranc } from '../lib/utils';
import { entityAPI, teamAPI } from '../lib/api';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Virtuoso } from 'react-virtuoso';
import { Search, Filter, Plus, Users, Flame, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import EntityAvatar from '../components/EntityAvatar';
import CategoryHeroCards from '../components/CategoryHeroCards';
import TrendingStrip from '../components/TrendingStrip';
import { AffiliationDisclaimer } from '../components/LegalDisclaimer';
import ConfirmDialog from '../components/ConfirmDialog';

// Fixed category order as specified
const ENTITY_TYPES = [
  { value: '', label: 'All Categories' },
  { value: 'country_leader', label: 'Rulers' },
  { value: 'ceo', label: 'CEOs' },
  { value: 'actor', label: 'Actors' },
  { value: 'athlete', label: 'Athletes' },
  { value: 'musician', label: 'Artists' },
  { value: 'influencer', label: 'Influencers' },
];

const ENTITY_TYPE_COLORS = {
  country_leader: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  ceo: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  actor: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  athlete: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  musician: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  influencer: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
};

// v5.7.58 — singular labels for the per-row category Badge. Matches the
// singular labels used on the top filter tabs (Ruler / Artist / Influencer).
// Without this map the row Badge crashed with `ReferenceError: Can't find
// variable: ENTITY_TYPE_SINGULAR` on initial render of the Search tab.
const ENTITY_TYPE_SINGULAR = {
  country_leader: 'Ruler',
  ceo: 'CEO',
  actor: 'Actor',
  athlete: 'Athlete',
  musician: 'Artist',
  influencer: 'Influencer',
};

// Sub-type labels per category
const SUB_TYPES = {
  athlete: [
    { value: 'soccer_player', label: 'Soccer' },
    { value: 'basketball_player', label: 'Basketball' },
    { value: 'tennis_player', label: 'Tennis' },
    { value: 'cricket_player', label: 'Cricket' },
    { value: 'general_athlete', label: 'Other Sports' },
  ],
  ceo: [
    { value: 'tech_ceo', label: 'Tech' },
    { value: 'finance_ceo', label: 'Finance' },
    { value: 'general_ceo', label: 'Other' },
  ],
  actor: [
    { value: 'film_actor', label: 'Film' },
    { value: 'tv_actor', label: 'TV' },
  ],
  musician: [
    { value: 'singer', label: 'Singer' },
    { value: 'rapper', label: 'Rapper' },
    { value: 'band_member', label: 'Band' },
  ],
  country_leader: [
    { value: 'president', label: 'President' },
    { value: 'prime_minister', label: 'PM' },
    { value: 'monarch', label: 'Monarch' },
  ],
  influencer: [
    { value: 'youtuber', label: 'YouTube' },
    { value: 'tiktoker', label: 'TikTok' },
    { value: 'streamer', label: 'Streamer' },
  ],
};

// ─── Memoized entity row (v5.7.37 perf pass) ───────────────────────────
//
// Why this exists: ExploreTab renders up to 50 entity rows. With every
// search keystroke (or sort change, or team mutation) the parent state
// updates and React would rebuild every row's tree by default. By
// extracting `EntityRow` and wrapping it in `React.memo`, only rows
// whose own props actually changed are re-rendered. Typing "Florian"
// goes from 50×7 = 350 row-renders down to ~50 (only when the new
// entity-list arrives).
const EntityRow = memo(function EntityRow({
  entity,
  isOnTeam,
  isAllowed,
  isFull,
  isDrafting,
  isRemoving,
  draftLocked,
  onOpen,
  onDraft,
  onDrop,
  colors,
}) {
  // v6.x (Task 0) — Draft availability. When this person's category
  // isn't draftable in the current (custom) league, fade the whole row
  // and swap the Draft button for a static, non-clickable "N/A" pill so
  // users instantly see who they cannot draft.
  const faded = !isOnTeam && !isAllowed;
  return (
    <div
      className={`flex items-center gap-3 bg-card border rounded-2xl px-4 py-3 transition-transform cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        faded
          ? 'border-border/40 opacity-55 grayscale-[0.35]'
          : 'border-border/60 hover:shadow-sm hover:border-border/80 active:scale-[0.99]'
      }`}
      role="button"
      tabIndex={0}
      aria-label={`View ${entity.name} details`}
      data-testid="entity-card"
      data-faded={faded ? 'true' : 'false'}
      onClick={() => onOpen?.(entity.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(entity.id); } }}
    >
      <EntityAvatar entity={entity} size={44} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{entity.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <Badge variant="secondary" className={`text-[9px] px-1.5 py-0 ${colors[entity.entity_type] || ''}`}>
            {ENTITY_TYPE_SINGULAR[entity.entity_type] || entity.entity_type?.replace('_', ' ')}
          </Badge>
          {(entity.metadata?.trending_score ?? 0) >= 55 && (
            <span
              className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-orange-500"
              data-testid="entity-card-hot-badge"
              title="Trending — attention spiking now"
            >
              <Flame className="h-2.5 w-2.5" /> Hot
            </span>
          )}
          {entity.sub_type && entity.sub_type !== 'general' && (
            <span className="text-[9px] text-muted-foreground">{entity.sub_type?.replace(/_/g, ' ')}</span>
          )}
          {entity.metadata?.country && (
            <span className="text-[10px] text-muted-foreground">{entity.metadata.country}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-right">
          <p className="text-sm font-bold tabular-nums">{formatFranc(entity.pricing?.price)}</p>
          <p className="text-[10px] text-muted-foreground flex items-center gap-0.5 justify-end">
            <Users className="h-2.5 w-2.5" />{entity.stats?.ownership_percentage?.toFixed(0) || 0}%
          </p>
        </div>
        {isOnTeam ? (
          // v5.7.57 — Drop button replaces the static "On Team" badge so
          // the user can remove a person from the Search/Explore list
          // directly. Stops the click from propagating to the parent
          // card-click (which would navigate to the person's profile).
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={(e) => { e.stopPropagation(); onDrop?.(entity.id, entity.name); }}
            disabled={isRemoving}
            data-testid="entity-card-drop-action"
          >
            {isRemoving ? '...' : 'Drop'}
          </Button>
        ) : !isAllowed ? (
          // v6.x (Task 0) — Static, non-clickable "N/A" pill replaces the
          // Draft button when this category isn't draftable in the league.
          <Button
            size="sm"
            variant="outline"
            disabled
            className="h-8 text-xs opacity-70 cursor-not-allowed pointer-events-none border-dashed"
            data-testid="entity-card-na-action"
            title="Not draftable in this league"
          >
            N/A
          </Button>
        ) : isFull ? (
          <span className="text-[9px] text-muted-foreground shrink-0">Full</span>
        ) : draftLocked ? (
          // Build 4 — the selected team is FINALIZED: hide "+Draft" (roster
          // changes go through Transfers/Manage Team, not free drafting).
          null
        ) : (
          <Button
            size="sm" variant="outline" className="h-8 text-xs"
            onClick={(e) => { e.stopPropagation(); onDraft?.(entity.id, entity.name, entity.pricing?.price); }}
            disabled={isDrafting}
            data-testid="entity-card-draft-action"
          >
            <Plus className="h-3 w-3 mr-1" />{isDrafting ? '...' : 'Draft'}
          </Button>
        )}
      </div>
    </div>
  );
});


// 2026-07-20 launch perf — react-virtuoso virtualization: only visible rows
// mount, no matter how many pages of "Load more" are appended. The Load More
// button lives in the virtual list's Footer so it renders after the last row.
const ExploreListFooter = ({ context }) => {
  if (!context || context.shown >= context.total) return <div className="h-1" />;
  return (
    <Button
      variant="outline"
      className="w-full"
      onClick={context.loadMore}
      disabled={context.loadingMore}
      data-testid="explore-load-more-button"
    >
      {context.loadingMore ? 'Loading…' : (
        <><ChevronDown className="h-4 w-4 mr-1" /> Load more ({context.shown} of {context.total})</>
      )}
    </Button>
  );
};
const EXPLORE_VIRTUOSO_COMPONENTS = { Footer: ExploreListFooter };

// PERF (2026-07 UX audit P1) — throttle redundant /teams/my refetches on
// tab switches. Mutations (draft/drop) always force-refresh.
let _teamFetchedAt = 0;

export default function ExploreTab({ onEntityClick, onTeamUpdate, currentTeam: passedTeam, leagueSettings, selectedLeagueId }) {
  const [entities, setEntities] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [category, setCategory] = useState('');
  const [subType, setSubType] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const entitiesRef = useRef([]);
  entitiesRef.current = entities;
  const [showSubTypes, setShowSubTypes] = useState(false);
  const [myTeam, setMyTeam] = useState(passedTeam);
  const [draftingId, setDraftingId] = useState(null);
  // v5.7.57 — Drop button (replaces "On Team" badge) needs its own
  // in-flight tracker so the row spinner shows during /teams/remove.
  const [removingId, setRemovingId] = useState(null);
  const [confirmDraft, setConfirmDraft] = useState(null); // {id, name, price}
  const [confirmDrop, setConfirmDrop] = useState(null);   // {id, name}
  const [sortBy, setSortBy] = useState('pricing.price');
  const [sortDir, setSortDir] = useState(-1);

  useEffect(() => { if (passedTeam) setMyTeam(passedTeam); }, [passedTeam]);

  // PERF (v5.7.37): debounce search input by 350ms. Previously every
  // keystroke fired a separate API call — typing "Florian" produced 7
  // sequential round-trips. Debouncing collapses them into one.
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadEntities = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = { limit: 25, skip: 0, sort_by: sortBy, sort_dir: sortDir };
      if (searchDebounced) params.search = searchDebounced;
      if (category) params.entity_type = category;
      if (subType) params.sub_type = subType;
      const res = await entityAPI.list(params);
      setEntities(res.data.entities || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); setLoadError(true); }
    finally { setLoading(false); }
  }, [searchDebounced, category, subType, sortBy, sortDir]);

  // 2026-07-20 perf audit — loadMore reads list length/total via refs so its
  // identity doesn't change per appended page (each new identity re-rendered
  // every memoized EntityRow through the button's onClick prop).
  const listStateRef = useRef({ len: 0, total: 0 });
  listStateRef.current.len = entities.length;
  listStateRef.current.total = total;
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    const { len, total: tot } = listStateRef.current;
    if (len >= tot) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const params = { limit: 25, skip: len, sort_by: sortBy, sort_dir: sortDir };
      if (searchDebounced) params.search = searchDebounced;
      if (category) params.entity_type = category;
      if (subType) params.sub_type = subType;
      const res = await entityAPI.list(params);
      // De-dupe on append (defense-in-depth alongside the backend's stable
      // sort tiebreaker) — a price tick between page fetches can still move
      // a row across the skip boundary.
      const seen = new Set(entitiesRef.current.map(x => x.id));
      const fresh = (res.data.entities || []).filter(x => x && !seen.has(x.id));
      if (fresh.length === 0) {
        // Nothing new (offset drift after dedupe) — cap total so the
        // Load More button hides instead of spinning forever.
        setTotal(entitiesRef.current.length);
      } else {
        setEntities(prev => [...prev, ...fresh]);
      }
    } catch (e) { console.error(e); }
    finally { loadingMoreRef.current = false; setLoadingMore(false); }
  }, [searchDebounced, category, subType, sortBy, sortDir]);

  const loadTeam = useCallback(async (force = false) => {
    // Always fetch fresh after a MUTATION (force=true) — the passedTeam
    // snapshot goes stale the moment we draft, transfer, or remove.
    // On plain mounts (tab switches) skip the refetch when the parent
    // passed a team AND we fetched within the last 10s: the optimistic
    // updates + parent's fws:team-refresh keep it current.
    if (!force && passedTeam?.id && Date.now() - _teamFetchedAt < 10_000) return;
    try {
      const res = await teamAPI.myTeams();
      _teamFetchedAt = Date.now();
      const teams = res.data.teams;
      if (teams?.length > 0) {
        // AUDIT #9 (2026-06) — pick the ACTIVE team. Build 4: the league
        // selected in the header is the source of truth (cross-league draft
        // bug fix); fall back to the passed team, then its league, then [0].
        const active = (selectedLeagueId && teams.find(t => t.league_id === selectedLeagueId))
          || (passedTeam?.id && teams.find(t => t.id === passedTeam.id))
          || (passedTeam?.league_id && teams.find(t => t.league_id === passedTeam.league_id))
          || teams[0];
        setMyTeam(active);
        if (onTeamUpdate) onTeamUpdate(active);
      }
    } catch (e) {
      // Non-fatal — loading the team list is a best-effort enrichment.
      // We log so it shows up in the console if it ever starts failing.
      console.warn('[ExploreTab] loadTeam failed:', e?.message || e);
    }
  }, [onTeamUpdate, passedTeam?.id, passedTeam?.league_id, selectedLeagueId]);

  useEffect(() => { loadEntities(); }, [loadEntities]);
  useEffect(() => { loadTeam(); }, [loadTeam]);

  const handleDraft = async (entityId, entityName) => {
    if (!myTeam) { toast.error('No team found.'); return; }
    setDraftingId(entityId);
    try {
      const drafted = await teamAPI.draft({
        team_id: myTeam.id,
        entity_id: entityId,
        // Build 4 — cross-league guard: server 409s when the header league
        // and this team's league drifted apart (stale UI).
        league_id: selectedLeagueId || myTeam.league_id,
      });
      toast.success(`Drafted ${entityName}!`);
      setConfirmDraft(null);
      // OPTIMISTIC UPDATE — append the new slot immediately so the
      // Draft button on the row flips to "On Team" before the API
      // round-trip completes. The follow-up `loadTeam()` below
      // reconciles with the server's canonical state.
      setMyTeam((prev) => prev ? {
        ...prev,
        slots: [...(prev.slots || []), {
          entity_id: entityId,
          entity_type: drafted?.data?.slot?.entity_type
            || entities.find(e => e.id === entityId)?.entity_type,
          acquired_at: new Date().toISOString(),
          is_captain: false,
        }],
        budget_remaining: drafted?.data?.budget_remaining ?? prev.budget_remaining,
      } : prev);
      await loadTeam(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to draft'); }
    finally { setDraftingId(null); }
  };

  const requestDraft = useCallback((entityId, entityName, price) => {
    setConfirmDraft({ id: entityId, name: entityName, price });
  }, []);

  // v5.7.57 — Drop a person from the user's roster directly from the
  // Search/Explore list (no need to bounce through the Team management
  // screen). Uses the same /teams/remove endpoint as TeamManagement.
  const handleDrop = async (entityId, entityName) => {
    if (!myTeam) { toast.error('No team found.'); return; }
    setRemovingId(entityId);
    try {
      const res = await teamAPI.remove({ team_id: myTeam.id, entity_id: entityId });
      const net_delta = res?.data?.net_delta || 0;
      toast.success(`Dropped ${entityName}`);
      setConfirmDrop(null);
      // Optimistic update: remove the slot locally so the button flips
      // back to "Draft" immediately.
      setMyTeam((prev) => prev ? {
        ...prev,
        slots: (prev.slots || []).filter((s) => s.entity_id !== entityId),
        budget_remaining: (prev.budget_remaining || 0) + net_delta,
      } : prev);
      await loadTeam(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to drop');
    } finally {
      setRemovingId(null);
    }
  };

  const requestDrop = useCallback((entityId, entityName) => {
    setConfirmDrop({ id: entityId, name: entityName });
  }, []);

  // PERF (v5.7.37): memoize league-derived lookups so they don't
  // re-compute on every render (search keystroke, sort change, etc.)
  // AND build O(1) maps so isOnTeam / isCategoryFull don't have to
  // scan myTeam.slots once per rendered row × per render.
  const ownedSet = useMemo(() => {
    return new Set((myTeam?.slots || []).map(s => s.entity_id));
  }, [myTeam?.slots]);

  const categoryCounts = useMemo(() => {
    const m = {};
    for (const s of (myTeam?.slots || [])) {
      m[s.entity_type] = (m[s.entity_type] || 0) + 1;
    }
    return m;
  }, [myTeam?.slots]);

  const { allowedSet, slotCap } = useMemo(() => {
    const slotCfg = leagueSettings?.slot_config || {};
    const hasCustom = Object.keys(slotCfg).length > 0;
    const allowedCats = hasCustom
      ? Object.entries(slotCfg).filter(([_, v]) => v > 0).map(([k]) => k)
      : (leagueSettings?.allowed_categories || null);
    const cap = (entityType) => hasCustom
      ? (slotCfg[entityType] || 0)
      : (leagueSettings?.required_per_category || 3);
    return {
      allowedSet: allowedCats ? new Set(allowedCats) : null,
      slotCap: cap,
    };
  }, [leagueSettings]);

  const isOnTeam = useCallback((entityId) => ownedSet.has(entityId), [ownedSet]);

  const isEntityAllowed = useCallback((entityType) => {
    if (!allowedSet) return true;
    return allowedSet.has(entityType);
  }, [allowedSet]);

  const isCategoryFull = useCallback((entityType) => {
    if (!myTeam) return false;
    return (categoryCounts[entityType] || 0) >= slotCap(entityType);
  }, [myTeam, categoryCounts, slotCap]);

  const availableSubTypes = category ? (SUB_TYPES[category] || []) : [];

  // 2026-08-15 (owner request) — search + sort stay frozen underneath the
  // sticky global header while hero cards / trending scroll away, so users
  // can always search or sort no matter how deep they've paged.
  const [stickyTop, setStickyTop] = useState(0);
  useEffect(() => {
    const measure = () => {
      const h = document.querySelector('[data-testid="global-header"]');
      setStickyTop(h ? h.offsetHeight : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div className="p-4" data-testid="explore-tab">
      <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
        <Search className="h-5 w-5 inline mr-2" /> Explore People
      </h2>

      {/* Category Hero Cards */}
      <div className="mb-3">
        <CategoryHeroCards
          onCategorySelect={(c) => { setCategory(c); setSubType(''); setShowSubTypes(false); }}
          selectedCategory={category}
        />
      </div>

      {/* P6 — Trending Now strip (real-world attention spikes → buy-low picks) */}
      <TrendingStrip onEntityClick={onEntityClick} />

      {/* Sticky controls — search + sort pinned under the global header
          (2026-08-15 owner request). Sub-type chips + count scroll normally. */}
      <div
        className="sticky z-30 -mx-4 px-4 pt-1 pb-2 bg-background/95 backdrop-blur-sm"
        style={{ top: stickyTop }}
        data-testid="explore-sticky-controls"
      >
        <div className="flex gap-2 mb-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search people..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="explore-search-input" />
          </div>
          {availableSubTypes.length > 0 && (
            <Button variant="outline" size="icon" onClick={() => setShowSubTypes(!showSubTypes)} data-testid="explore-open-filters">
              <Filter className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Sort Options
            v6.1.17.7 — "Form" replaced with "Recent Form" backed by
            `stats.weekly_points` (rolling 7-day, reset on weekly cron). */}
        <div className="flex gap-1.5 overflow-x-auto" data-testid="explore-sort-row">
          {[
            { key: 'metadata.trending_score', label: '🔥 Trending' },
            { key: 'pricing.price', label: 'Price' },
            { key: 'stats.weekly_points', label: 'Recent Form' },
            { key: 'stats.ownership_percentage', label: 'Owned' },
            { key: 'name', label: 'A-Z', dir: 1 },
            { key: 'stats.total_points', label: '₣ Earned' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => {
                if (sortBy === opt.key) { setSortDir((d) => -d); }
                else { setSortBy(opt.key); setSortDir(opt.dir || -1); }
              }}
              className={`shrink-0 px-2.5 py-1 text-[10px] rounded-full border transition-colors ${
                sortBy === opt.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-border'
              }`}
              data-testid={`explore-sort-${opt.key}`}
            >
              {opt.label} {sortBy === opt.key && (sortDir === -1 ? '↓' : '↑')}
            </button>
          ))}
        </div>
      </div>

      {/* Sub-type filters (shown when filter icon clicked AND a category is selected) */}
      {showSubTypes && availableSubTypes.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            onClick={() => setSubType('')}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              !subType ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-border'
            }`}
          >
            All {ENTITY_TYPES.find(t => t.value === category)?.label || ''}
          </button>
          {availableSubTypes.map((st) => (
            <button
              key={st.value}
              onClick={() => setSubType(st.value)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                subType === st.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-secondary-foreground border-border'
              }`}
              data-testid={`explore-subtype-${st.value}`}
            >
              {st.label}
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground mb-2">{total} people found</p>

      {/* Entity Grid — virtualized (react-virtuoso): only visible rows mount */}
      {loading ? (
        <div className="space-y-3 h-[calc(100vh-420px)] overflow-hidden">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-20 w-full rounded-2xl" />)}</div>
      ) : loadError ? (
        <div className="h-[calc(100vh-420px)] flex flex-col items-center justify-center text-center" data-testid="explore-load-error">
          <p className="text-sm text-muted-foreground mb-3">Failed to load people. Please try again.</p>
          <Button variant="outline" size="sm" onClick={loadEntities} data-testid="explore-retry-btn">Retry</Button>
        </div>
      ) : (
        <Virtuoso
          style={{ height: 'calc(100vh - 420px)' }}
          data={entities}
          computeItemKey={(_, entity) => entity.id}
          increaseViewportBy={{ top: 200, bottom: 400 }}
          components={EXPLORE_VIRTUOSO_COMPONENTS}
          context={{ loadMore, loadingMore, shown: entities.length, total }}
          itemContent={(_, entity) => (
            <div className="pb-2">
              <EntityRow
                entity={entity}
                isOnTeam={ownedSet.has(entity.id)}
                isAllowed={isEntityAllowed(entity.entity_type)}
                isFull={isCategoryFull(entity.entity_type)}
                isDrafting={draftingId === entity.id}
                isRemoving={removingId === entity.id}
                draftLocked={!!myTeam?.finalized}
                onOpen={onEntityClick}
                onDraft={requestDraft}
                onDrop={requestDrop}
                colors={ENTITY_TYPE_COLORS}
              />
            </div>
          )}
        />
      )}
      <div className="mt-3">
        <AffiliationDisclaimer testId="explore-affiliation-disclaimer" />
      </div>

      {/* Draft confirmation dialog */}
      <ConfirmDialog
        open={!!confirmDraft}
        title="Draft Person"
        description={confirmDraft ? `Draft ${confirmDraft.name} for ${formatFranc(confirmDraft.price)}? This will deduct from your budget.` : ''}
        confirmLabel="Draft"
        cancelLabel="Cancel"
        onConfirm={() => confirmDraft && handleDraft(confirmDraft.id, confirmDraft.name)}
        onCancel={() => setConfirmDraft(null)}
      />

      {/* v5.7.57 — Drop confirmation dialog (mirrors the TeamManagement flow). */}
      <ConfirmDialog
        open={!!confirmDrop}
        title="Drop Person"
        description={confirmDrop ? (myTeam?.finalized
          ? `Drop ${confirmDrop.name} from your roster? You'll be refunded their current market price (buy low, sell high — a depreciated player refunds less).`
          : `Drop ${confirmDrop.name} from your roster? You'll be refunded exactly what you paid (no penalty during the draft phase).`) : ''}
        confirmLabel="Drop"
        cancelLabel="Keep"
        variant="destructive"
        onConfirm={() => confirmDrop && handleDrop(confirmDrop.id, confirmDrop.name)}
        onCancel={() => setConfirmDrop(null)}
      />
    </div>
  );
}
